import type { ReadOnlyTextField } from '../services/api'
import type { PersonalData, AddressData } from '../types'
import { sanitizeForYousign } from './validation'
import { formatPhoneForYousign } from './yousignFields'

export interface BuildFieldsParams {
  patientNumber: string
  personalData: PersonalData
  phoneNumber: string
  addressData: AddressData
}

/** Format date string as DD.MM.YYYY */
export function formatDateDE(dateStr: string): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

/** Build the read_only_text_fields array for Yousign SR creation */
export function buildReadOnlyTextFields({ patientNumber, personalData, phoneNumber, addressData }: BuildFieldsParams): ReadOnlyTextField[] {
  const getSalutation = (): string => {
    if (personalData.gender === 'male') return 'Herr'
    if (personalData.gender === 'female') return 'Frau'
    return ''
  }

  const patientName = sanitizeForYousign(`${personalData.firstName} ${personalData.lastName}`)
  const patientNameWithSalutation = sanitizeForYousign(
    getSalutation()
      ? `${getSalutation()} ${personalData.firstName} ${personalData.lastName}`
      : `${personalData.firstName} ${personalData.lastName}`
  )
  const patientPhone = sanitizeForYousign(formatPhoneForYousign(phoneNumber))
  const patientEmail = sanitizeForYousign(personalData.email || '')
  const patientBirthdate = formatDateDE(personalData.birthDate)
  const patientCity = sanitizeForYousign(addressData.city)

  const fullAddress = sanitizeForYousign(
    `${addressData.address}, ${addressData.postalCode} ${addressData.city}, ${addressData.country}`
  )

  const isMale = personalData.gender === 'male' ? 'x' : ' '
  const isFemale = personalData.gender === 'female' ? 'x' : ' '
  const isDiverse = personalData.gender === 'diverse' ? 'x' : ' '

  const today = new Date()
  const signDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`

  return [
    // Document 1 fields
    { label: 'patient_pid_1', text: sanitizeForYousign(patientNumber) },
    { label: 'patient_namewithsal_1', text: patientNameWithSalutation },
    { label: 'patient_name_1', text: patientName },
    { label: 'patient_address_1', text: fullAddress },
    { label: 'patient_email_1', text: patientEmail },
    { label: 'sign_patientcity_1', text: patientCity },
    { label: 'sign_date_1_1', text: signDate },
    { label: 'sign_date_1_2', text: signDate },

    // Document 2 fields
    { label: 'patient_pid_2', text: sanitizeForYousign(patientNumber) },
    { label: 'patient_namewithsal_2', text: patientNameWithSalutation },
    { label: 'patient_name_2', text: patientName },
    { label: 'sign_patientcity_2', text: patientCity },
    { label: 'sign_date_2_1', text: signDate },

    // Document 3 fields
    { label: 'patient_name_3', text: patientName },
    { label: 'patient_male_3', text: isMale },
    { label: 'patient_female_3', text: isFemale },
    { label: 'patient_diverse_3', text: isDiverse },
    { label: 'patient_phone_3', text: patientPhone },
    { label: 'patient_street_3', text: sanitizeForYousign(addressData.address) },
    { label: 'patient_zip_3', text: sanitizeForYousign(addressData.postalCode) },
    { label: 'patient_city_3', text: patientCity },
    { label: 'patient_country_3', text: sanitizeForYousign(addressData.country) },
    { label: 'patient_birthdate_3', text: patientBirthdate },
    { label: 'sign_patientcity_3', text: patientCity },
    { label: 'sign_date_3_1', text: signDate },
  ]
}

/** Compute a deterministic hash string from read_only_text_fields for change detection.
 * A real hash (FNV-1a 64-bit hex) — safe to persist and send to the server ledger,
 * unlike the raw field JSON which contains PII. */
export function computeYousignDataHash(fields: ReadOnlyTextField[]): string {
  // Exclude sign_date fields from hash since they change daily and would cause
  // unnecessary SR recreation if the user just navigates back and forth same day
  // The date will be correct when the SR is actually created
  const stableFields = fields.filter(f => !f.label.startsWith('sign_date_'))
  const input = JSON.stringify(stableFields)

  let hash = 0xcbf29ce484222325n
  const prime = 0x100000001b3n
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i))
    hash = (hash * prime) & 0xffffffffffffffffn
  }
  return hash.toString(16).padStart(16, '0')
}
